import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ShieldCheck, Plus, X } from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { fetchWalls, erectWall, liftWall, type EthicalWall } from '@/lib/ethicalWallsApi';
import { fetchMatters } from '@/lib/mattersApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

function MatterLine({ m }: { m: EthicalWall['matterA'] }) {
  return (
    <span className="text-foreground">
      <span className="font-medium">{m.caseName}</span>{' '}
      <span className="text-xs text-muted-foreground">{m.caseNumber}</span>
    </span>
  );
}

export default function EthicalWallsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const walls = useQuery({ queryKey: ['ethical-walls'], queryFn: () => fetchWalls() });
  const rows = walls.data ?? [];
  const active = rows.filter((w) => w.status === 'active');
  const lifted = rows.filter((w) => w.status === 'lifted');

  const lift = useMutation({
    mutationFn: (id: string) => liftWall(id),
    onSuccess: () => {
      toast.success('Barrier lifted');
      qc.invalidateQueries({ queryKey: ['ethical-walls'] });
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Information Barriers"
        subtitle="Ethical walls between two matters whose members act on opposing sides of the same case. Each side is segregated; solicitors are notified."
        actions={
          <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
            Erect barrier
          </Button>
        }
      />

      {walls.isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            No information barriers. Erect one when two members of chambers are instructed on opposing sides of the same case.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          <section>
            <h2 className="mb-2 text-sm font-semibold text-foreground">Active ({active.length})</h2>
            {active.length === 0 ? (
              <p className="text-sm text-muted-foreground">None active.</p>
            ) : (
              <div className="space-y-2">
                {active.map((w) => (
                  <Card key={w.id}>
                    <CardContent className="flex items-start justify-between gap-4 p-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <ShieldCheck className="h-4 w-4 text-brand-600 dark:text-brand-400" weight="fill" />
                          <MatterLine m={w.matterA} />
                          <span className="text-xs text-muted-foreground">⟷ walled from ⟷</span>
                          <MatterLine m={w.matterB} />
                        </div>
                        {w.reason && <p className="text-xs text-muted-foreground">{w.reason}</p>}
                        <p className="text-xs text-muted-foreground">
                          Erected {new Date(w.erectedAt).toLocaleDateString()} ·{' '}
                          {w.notifiedSolicitorsAt ? 'Solicitors notified' : 'Solicitors NOT yet notified'}
                        </p>
                      </div>
                      <Button variant="secondary" size="sm" onClick={() => lift.mutate(w.id)} disabled={lift.isPending}>
                        Lift
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </section>

          {lifted.length > 0 && (
            <section>
              <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Lifted ({lifted.length})</h2>
              <div className="space-y-2">
                {lifted.map((w) => (
                  <Card key={w.id} className="opacity-70">
                    <CardContent className="flex items-center gap-2 p-4 text-sm">
                      <MatterLine m={w.matterA} />
                      <span className="text-xs text-muted-foreground">⟷</span>
                      <MatterLine m={w.matterB} />
                      <span className="ml-auto text-xs text-muted-foreground">
                        Lifted {w.liftedAt ? new Date(w.liftedAt).toLocaleDateString() : ''}
                      </span>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {creating && <ErectModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function ErectModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [matterAId, setMatterAId] = useState('');
  const [matterBId, setMatterBId] = useState('');
  const [reason, setReason] = useState('');
  const [notified, setNotified] = useState(false);

  const mattersQ = useQuery({ queryKey: ['matters', 'wall-picker'], queryFn: () => fetchMatters() });
  const options = useMemo(
    () => (mattersQ.data?.matters ?? []).map((m) => ({ value: m.id, label: `${m.name} · ${m.number}` })),
    [mattersQ.data],
  );

  const erect = useMutation({
    mutationFn: () => erectWall({ matterAId, matterBId, reason: reason.trim() || undefined, solicitorsNotified: notified }),
    onSuccess: () => {
      toast.success('Barrier erected — the two sides are now segregated');
      qc.invalidateQueries({ queryKey: ['ethical-walls'] });
      onClose();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const invalid = !matterAId || !matterBId || matterAId === matterBId;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <ScrollLock />
      <div className="my-auto w-full max-w-lg rounded-lg bg-background p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Erect an information barrier</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">
          Wall two matters whose members are on opposing sides of the same case. Each side's clerk team is split and the
          matters are mutually sealed.
        </p>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Matter — side A</label>
            <SearchableSelect value={matterAId} onChange={setMatterAId} options={options} placeholder="Select a matter…" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Matter — side B</label>
            <SearchableSelect value={matterBId} onChange={setMatterBId} options={options} placeholder="Select the opposing matter…" />
          </div>
          {matterAId && matterAId === matterBId && (
            <p className="text-xs text-destructive">A barrier needs two different matters.</p>
          )}
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Reason (optional)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              placeholder="e.g. both instructed in Acme v Sterling"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={notified} onChange={(e) => setNotified(e.target.checked)} />
            The instructing solicitors have been notified of the barrier
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" size="md" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="md" onClick={() => erect.mutate()} disabled={invalid || erect.isPending}>
            {erect.isPending ? 'Erecting…' : 'Erect barrier'}
          </Button>
        </div>
      </div>
    </div>
  );
}
